import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Collapse,
  Group,
  Paper,
  Stack,
  Text,
} from '@cowbell/ui/core';
import {
  confirmAsyncTaskProposal,
  getAsyncTaskProposalState,
  rejectAsyncTaskProposal,
} from '../../api/async-tasks.api';
import { OutputChannelsEditor } from '../outputs/OutputChannelsEditor';
import type { OutputChannel } from '../../types/moo-tasks.types';
import AsyncTaskProgress from './AsyncTaskProgress';

interface AsyncTaskProposalProps {
  readonly proposalId: string;
  readonly name: string;
  readonly instructions: string;
  readonly conversationId?: string;
  /** Chat-message identity, handed to the progress card this swaps to. */
}

/** HITL confirmation card for start_async_task: nothing runs until the user
 *  reviews the task and confirms. Delivery channels are optional extras
 *  (same editor as the Task Automation wizard); results always land in chat.
 *  On confirm the card swaps to the live progress view; on cancel the
 *  proposal is discarded server-side. */
export default function AsyncTaskProposal({
  proposalId,
  name,
  instructions,
  conversationId,
}: AsyncTaskProposalProps) {
  const convId = conversationId || '';

  const [outputs, setOutputs] = useState<OutputChannel[]>([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rejected, setRejected] = useState(false);
  const [expired, setExpired] = useState(false);
  const [startedRunId, setStartedRunId] = useState('');

  // Rehydrate on mount: started/cancelled live only in component state, so a
  // conversation switch remounts this card back to "Confirm & start" even
  // when the run is already going (clicking again then "starts" it a second
  // time via the confirm idempotency cache). Render the confirm view
  // optimistically (pending is the common case) and swap when the server
  // reports a terminal state.
  useEffect(() => {
    // Identity changed (same component instance reused for another
    // proposal): drop every lifecycle flag from the previous one, or a
    // terminal state would stick to a proposal it doesn't belong to.
    setStartedRunId('');
    setRejected(false);
    setExpired(false);
    setError('');
    if (!convId || !proposalId) return undefined;
    let active = true;
    getAsyncTaskProposalState(convId, proposalId)
      .then(({ data }) => {
        if (!active) return;
        if (data.status === 'confirmed' && data.run_id) {
          setStartedRunId(data.run_id);
        } else if (data.status === 'rejected') {
          setRejected(true);
        } else if (data.status === 'expired') {
          setExpired(true);
        }
      })
      .catch(() => {
        // Hydration is best-effort — a failure leaves the normal confirm
        // flow, whose own error handling covers a truly dead proposal.
      });
    return () => {
      active = false;
    };
  }, [convId, proposalId]);

  // Confirmed: hand over to the normal progress card.
  if (startedRunId) {
    return (
      <AsyncTaskProgress
        runId={startedRunId}
        name={name}
        conversationId={convId || undefined}
      />
    );
  }

  if (rejected || expired) {
    return (
      <Paper withBorder p="md" radius="md">
        <Group gap="xs">
          <Badge size="sm" color="gray" variant="light" radius="sm">
            {rejected ? 'Cancelled' : 'Expired'}
          </Badge>
          <Text size="sm" c="dimmed">
            {rejected
              ? `${name} — not started.`
              : `${name} — this confirmation expired. Ask again in chat to get a new one.`}
          </Text>
        </Group>
        {error ? (
          // Hydration can land a terminal state while a confirm/reject was
          // still in flight — its failure must stay visible, not vanish
          // under the terminal layout.
          <Text size="sm" c="red" mt="xs">
            {error}
          </Text>
        ) : null}
      </Paper>
    );
  }

  // Without a conversation id the proposal endpoints can't be called at all —
  // say so instead of silently ignoring the click (the card can render before
  // the shell/provider has resolved the conversation).
  const requireConversation = () => {
    if (convId) return true;
    setError(
      'No conversation context — reopen this conversation and try again.'
    );
    return false;
  };

  const handleConfirm = async () => {
    if (busy || !requireConversation()) return;
    setBusy(true);
    setError('');
    try {
      const res = await confirmAsyncTaskProposal(convId, proposalId, {
        data: { outputs },
      });
      setStartedRunId(res.data.run_id);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      setError(
        detail ||
          'Could not start the task. It may have expired — ask again in chat.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (busy || !requireConversation()) return;
    setBusy(true);
    setError('');
    try {
      await rejectAsyncTaskProposal(convId, proposalId);
      // Only claim "Cancelled" when the server actually discarded it — a
      // failed reject leaves the proposal confirmable, and showing the
      // cancelled state here would misrepresent that.
      setRejected(true);
    } catch {
      setError('Could not cancel — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} size="sm">
            {name}
          </Text>
          <Badge size="sm" color="yellow" variant="light" radius="sm">
            Awaiting confirmation
          </Badge>
        </Group>
        <button
          type="button"
          onClick={() => setShowInstructions((v) => !v)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            width: 'fit-content',
          }}
        >
          <Text size="xs" c="blue">
            {showInstructions ? 'Hide' : 'Show'} task instructions
          </Text>
        </button>
        <Collapse in={showInstructions}>
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
            {instructions}
          </Text>
        </Collapse>

        <OutputChannelsEditor
          outputs={outputs}
          onChange={setOutputs}
          addChannelLabel="+ Add result delivery channel (optional)"
        />

        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
        <Group>
          <Button size="compact-sm" loading={busy} onClick={handleConfirm}>
            Confirm &amp; start
          </Button>
          <Button
            size="compact-sm"
            variant="subtle"
            color="gray"
            disabled={busy}
            onClick={handleReject}
          >
            Cancel
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
